<template>
  <PrintPageLandscape>
    <PresentationPage :page-number="pageNumber" :page-count="pageCount">
      <p class="presentation-page__eyebrow">Cashflow-Analyse</p>
      <h2 class="presentation-page__title">Wirtschaftlichkeitsbetrachtung — drei Szenarien</h2>
      <p class="presentation-page__text">
        Cashflow über 20 Jahre · Garantie- / Median- / Optimalfall · mit Fremdfinanzierung.
      </p>
      <div class="presentation-page__scenario-cards">
        <article
          v-for="scenario in scenarios"
          :key="scenario.name"
          :class="['presentation-page__scenario-card', scenario.cardClass]"
        >
          <div :class="['presentation-page__scenario-card-header', scenario.headerClass]">
            {{ scenario.name }}
          </div>
          <div :class="['presentation-page__scenario-card-body', scenario.bodyClass]">
            <p :class="['presentation-page__scenario-card-rate', scenario.rateClass]">
              {{ scenario.rate }}
            </p>
            <p class="presentation-page__scenario-card-label">Einspeisetarif</p>
            <div class="presentation-page__scenario-card-accent" aria-hidden="true"></div>

            <div
              v-for="row in scenario.rows"
              :key="row.label"
              class="presentation-page__scenario-card-row"
            >
              <span class="presentation-page__scenario-card-row-label">{{ row.label }}</span>
              <span class="presentation-page__scenario-card-row-value">{{ row.value }}</span>
            </div>
          </div>
        </article>
      </div>
      <p class="presentation-page__disclaimer">
        Musterberechnung ohne Haftung auf Richtigkeit. Keine Steuer- oder Rechtsberatung.
      </p>
    </PresentationPage>
  </PrintPageLandscape>
</template>

<script>
import PrintPageLandscape from '@/components/Print/PrintPageLandscape.vue';
import PresentationPage from '@/views/MasterPresentation/PresentationPage';
import { formatNumber, NUMBER_FORMAT, parseFiniteNumber } from '@/helpers/numberFormat';

const scenarioRows = [
  { label: 'EEG-Erlöse (20 J.)', key: 'revenue' },
  { label: 'Ø EK-Rendite n. Steuer', key: 'returnRate' },
  { label: 'Ø Cashflow p.a.', key: 'cashflowYear' },
  { label: 'Ø Cashflow p.m.', key: 'cashflowMonth' },
  { label: 'Nettogewinn (20 J.)', key: 'netProfit' },
];

const scenarioDefinitions = [
  {
    name: 'Garantiefall',
    finModelKey: 'guarantee',
    feedInTariffFormat: NUMBER_FORMAT.DECIMAL_QUADRUPLE,
  },
  {
    name: 'Medianfall',
    finModelKey: 'median',
    headerClass: 'presentation-page__scenario-card-header--brand-teal',
    bodyClass: 'presentation-page__scenario-card-body--brand-teal',
    rateClass: 'presentation-page__scenario-card-rate--brand-teal',
    feedInTariffFormat: NUMBER_FORMAT.DECIMAL,
  },
  {
    name: 'Optimalfall',
    finModelKey: 'optimal',
    feedInTariffFormat: NUMBER_FORMAT.DECIMAL,
  },
];

function formatScenarioRowValue(scenarioData, rowKey) {
  if (!scenarioData) {
    return formatNumber(null, NUMBER_FORMAT.CURRENCY);
  }

  switch (rowKey) {
    case 'revenue':
      return formatNumber(scenarioData.eegRevenues, NUMBER_FORMAT.CURRENCY);
    case 'returnRate': {
      const preTaxReturn = parseFiniteNumber(scenarioData.preTaxReturn);
      const preTaxReturnPercent = preTaxReturn === null ? null : preTaxReturn * 100;
      return `${formatNumber(preTaxReturnPercent, NUMBER_FORMAT.DECIMAL)} % p.a.`;
    }
    case 'cashflowYear':
      return `Ø ${formatNumber(scenarioData.preTaxCashflowPerYear, NUMBER_FORMAT.CURRENCY)}`;
    case 'cashflowMonth':
      return `Ø ${formatNumber(scenarioData.preTaxCashflowPerMonth, NUMBER_FORMAT.CURRENCY)}`;
    case 'netProfit':
      return formatNumber(scenarioData.preTaxProfitOver20Years, NUMBER_FORMAT.CURRENCY);
    default:
      return formatNumber(null, NUMBER_FORMAT.CURRENCY);
  }
}

export default {
  name: 'MasterPresentationPrintPage38',
  components: { PrintPageLandscape, PresentationPage },
  props: {
    project: { type: Object, default: null },
    pageCount: { type: Number, default: null },
    pageNumber: { type: Number, default: 0 },
  },
  computed: {
    finModelScenarios() {
      return this.project?.finModel?.scenarios ?? {};
    },

    scenarios() {
      return scenarioDefinitions.map((definition) => {
        const scenarioData = this.finModelScenarios[definition.finModelKey];

        return {
          name: definition.name,
          headerClass: definition.headerClass,
          bodyClass: definition.bodyClass,
          rateClass: definition.rateClass,
          cardClass: definition.cardClass,
          rate: `${formatNumber(scenarioData?.feedInTariff, definition.feedInTariffFormat)} €/kWh`,
          rows: scenarioRows.map((row) => ({
            label: row.label,
            value: formatScenarioRowValue(scenarioData, row.key),
          })),
        };
      });
    },
  },
};
</script>
